import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  People,
  CreditCard,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/apiService';
import {
  getManageAccountCacheKey,
  readManageAccountCache,
  writeManageAccountCache,
  clearManageAccountCache,
  MANAGE_ACCOUNT_SUBSCRIPTION_CHANGED_EVENT,
} from './manageAccountSessionCache';
import { parseApiDateTime } from '../../utils/dateTimeUtils';
import { resolveSubscriptionAccessView } from './subscriptionAccessDisplay';

/** FREE plan users should show Active, not org-level PENDING from pre-activation state. */
function resolveSubscriptionDisplayStatus(sub?: {
  planType?: string;
  displayPlan?: string;
  status?: string;
  subscriptionStatus?: string;
}): string {
  if (!sub) return 'ACTIVE';
  const plan = (sub.displayPlan || sub.planType || '').toUpperCase();
  const raw = (sub.status || sub.subscriptionStatus || 'ACTIVE').toUpperCase();
  if (plan === 'FREE' && (raw === 'PENDING' || raw === 'INACTIVE')) {
    return 'ACTIVE';
  }
  return sub.status || sub.subscriptionStatus || 'ACTIVE';
}

type DashboardStats = {
  licenseUsage?: { total: number; used: number; available: number; displayPlan?: string };
  subscription?: {
    planType: string;
    displayPlan?: string;
    status: string;
    subscriptionStatus?: string;
    subscriptionBillingStatus?: string;
    billingCycle?: string;
    nextBillingDate?: number;
    currentPeriodStart?: number;
    currentPeriodEnd?: number;
    validUntil?: string;
    contactSales?: boolean;
  };
  teamMembers?: { total: number; pending: number };
  recentActivity?: Array<{ type: string; message: string; date: string }>;
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const dashboardCacheKey = `manageAccountDashboard:${user?.id ?? 'anonymous'}`;
  const cachedStats = readManageAccountCache<DashboardStats>('dashboard', dashboardCacheKey);
  const [loading, setLoading] = useState(!cachedStats);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>(cachedStats ?? {});

  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(dashboardCacheKey);
      if (!cached) return;
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') {
        setStats(parsed);
        setLoading(false);
      }
    } catch {
      // Ignore cache read errors to preserve existing behavior.
    }
  }, [dashboardCacheKey]);

  // Auto-hide error message after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [error]);

  const loadDashboardData = useCallback(async () => {
    const hasCache = !!readManageAccountCache<DashboardStats>('dashboard', dashboardCacheKey);
    try {
      const hasCachedStats = (() => {
        try {
          const cached = sessionStorage.getItem(dashboardCacheKey);
          return !!cached;
        } catch {
          return false;
        }
      })();
      if (!hasCachedStats) {
        setLoading(true);
      }
      setError(null);

      const [licenseResult, subscriptionResult, teamResult] = await Promise.allSettled([
        apiService.getLicenseUsage(),
        apiService.getSubscription(),
        apiService.getTeamMembers(),
      ]);

      const licenseResponse = licenseResult.status === 'fulfilled' ? licenseResult.value : undefined;
      const licenseData = licenseResponse && !licenseResponse.error
        ? {
            total: licenseResponse.totalLicenses || 0,
            used: licenseResponse.usedLicenses || 0,
            available: licenseResponse.availableLicenses || 0,
            displayPlan: licenseResponse.displayPlan || licenseResponse.planType || 'FREE',
          }
        : undefined;

      const subscriptionResponse = subscriptionResult.status === 'fulfilled' ? subscriptionResult.value : undefined;

      const subscriptionData = subscriptionResponse && !subscriptionResponse.error
        ? {
            planType: subscriptionResponse.planType || user?.planType || '',
            displayPlan: subscriptionResponse.displayPlan || subscriptionResponse.planType || user?.planType || '',
            status: resolveSubscriptionDisplayStatus({
              planType: subscriptionResponse.planType || user?.planType,
              displayPlan: subscriptionResponse.displayPlan || subscriptionResponse.planType,
              status: subscriptionResponse.status,
              subscriptionStatus: subscriptionResponse.subscriptionStatus,
            }),
            subscriptionStatus: subscriptionResponse.subscriptionStatus,
            subscriptionBillingStatus: subscriptionResponse.subscriptionBillingStatus,
            billingCycle: subscriptionResponse.billingCycle, // 'monthly' or 'yearly'
            nextBillingDate: subscriptionResponse.nextBillingDate,
            currentPeriodStart:
              subscriptionResponse.currentPeriodStart ?? subscriptionResponse.current_period_start,
            currentPeriodEnd:
              subscriptionResponse.currentPeriodEnd ?? subscriptionResponse.current_period_end,
            validUntil: subscriptionResponse.validUntil ?? subscriptionResponse.valid_until,
          }
        : {
            planType: user?.planType || '',
            displayPlan: user?.planType || '',
            status: 'ACTIVE',
          };

      const teamResponse = teamResult.status === 'fulfilled' ? teamResult.value : { members: [] as any[] };
      const membersArray = teamResponse.members || [];
      const memberConsumesLicenseSeat = (m: any) => {
        const role = (m?.role ?? '').toString();
        if (role === 'ADMIN') return false;
        if (role === 'ADMIN_MEMBER' && !m?.addedByEmail) return false;
        return true;
      };
      const teamData = {
        total: membersArray.filter(memberConsumesLicenseSeat).length,
        pending: membersArray.filter((m: any) => m.status === 'PENDING').length,
      };

      if (licenseResult.status === 'rejected') {
        const message =
          (licenseResult.reason as any)?.response?.data?.error ||
          (licenseResult.reason as any)?.message;
        if (message) setError(message);
      }

      const planLabel = subscriptionData?.planType || subscriptionData?.displayPlan || 'Unknown';
      const disp = (subscriptionData?.displayPlan || '').toUpperCase();
      const planTypeUpper = (subscriptionData?.planType || '').toUpperCase();
      const isFreePlanUi = disp === 'FREE' || planTypeUpper === 'FREE';
      const accessView = resolveSubscriptionAccessView(subscriptionData, planLabel);
      const subscriptionActivityMessage = accessView.activityMessage;

      const nextStats: DashboardStats = {
        licenseUsage: licenseData,
        subscription: subscriptionData,
        teamMembers: teamData,
        recentActivity: [
          {
            type: 'subscription',
            message: subscriptionActivityMessage,
            date:
              subscriptionData?.currentPeriodStart != null
                ? new Date(subscriptionData.currentPeriodStart * 1000).toISOString()
                : isFreePlanUi && user?.createdAt
                  ? parseApiDateTime(user.createdAt)?.toISOString() ?? ''
                  : '',
          },
          {
            type: 'team',
            message: `${teamData.total} team member${teamData.total !== 1 ? 's' : ''}`,
            date: '',
          },
        ],
      };
      setStats(nextStats);
      writeManageAccountCache('dashboard', dashboardCacheKey, nextStats);

      void apiService.getSubscriptionDetails().then((detailsPayload) => {
        if (!detailsPayload || (detailsPayload as { error?: string }).error) {
          return;
        }
        const planFromLimits = detailsPayload.plan;
        setStats((prev) => {
          const sub = prev.subscription;
          if (!sub) {
            return prev;
          }
          const enriched = {
            ...prev,
            subscription: {
              ...sub,
              planType: sub.planType || planFromLimits || user?.planType || '',
              displayPlan: sub.displayPlan || planFromLimits || sub.planType || user?.planType || '',
              contactSales: detailsPayload.contactSales,
            },
          };
          writeManageAccountCache('dashboard', dashboardCacheKey, enriched);
          return enriched;
        });
      }).catch(() => {
        /* optional enrichment */
      });
      try {
        sessionStorage.setItem(
          dashboardCacheKey,
          JSON.stringify({
            licenseUsage: licenseData,
            subscription: subscriptionData,
            teamMembers: teamData,
            recentActivity: [
              {
                type: 'subscription',
                message: subscriptionActivityMessage,
                date:
                  subscriptionData?.currentPeriodStart != null
                    ? new Date(subscriptionData.currentPeriodStart * 1000).toISOString()
                    : isFreePlanUi && user?.createdAt
                      ? parseApiDateTime(user.createdAt)?.toISOString() ?? ''
                      : '',
              },
              {
                type: 'team',
                message: `${teamData.total} team member${teamData.total !== 1 ? 's' : ''}`,
                date: '',
              },
            ],
          })
        );
      } catch {
        // Ignore cache write errors to preserve existing behavior.
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [dashboardCacheKey, user]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    const onSubscriptionChanged = () => {
      clearManageAccountCache('dashboard', dashboardCacheKey);
      try {
        sessionStorage.removeItem(dashboardCacheKey);
      } catch {
        // Ignore cache clear errors to preserve existing behavior.
      }
      void loadDashboardData();
    };
    window.addEventListener(MANAGE_ACCOUNT_SUBSCRIPTION_CHANGED_EVENT, onSubscriptionChanged);
    return () =>
      window.removeEventListener(MANAGE_ACCOUNT_SUBSCRIPTION_CHANGED_EVENT, onSubscriptionChanged);
  }, [dashboardCacheKey, loadDashboardData]);

  useEffect(() => {
    const onTeamMembersChanged = () => {
      clearManageAccountCache('dashboard', dashboardCacheKey);
      try {
        sessionStorage.removeItem(dashboardCacheKey);
      } catch {
        // Ignore cache clear errors to preserve existing behavior.
      }
      void loadDashboardData();
    };
    window.addEventListener('manageAccount:teamMembersChanged', onTeamMembersChanged);
    return () =>
      window.removeEventListener('manageAccount:teamMembersChanged', onTeamMembersChanged);
  }, [dashboardCacheKey, loadDashboardData]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'cancelled':
        return 'warning';
      case 'canceled':
      case 'expired':
      case 'inactive':
        return 'error';
      case 'past_due':
        return 'warning';
      default:
        return 'default';
    }
  };

  /** Display-only: Unix seconds from API (Stripe), or backend LocalDateTime (naive = UTC). */
  const formatSubscriptionDate = (value: number | string | undefined | null) => {
    const dateOpts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
    if (value == null || value === '') return 'N/A';
    if (typeof value === 'number' && !Number.isNaN(value)) {
      // Stripe / API period bounds are epoch seconds.
      return new Date(value * 1000).toLocaleDateString('en-US', dateOpts);
    }
    if (typeof value === 'string') {
      const t = value.trim();
      if (/^\d+$/.test(t)) {
        const sec = Number(t);
        if (!Number.isNaN(sec)) return new Date(sec * 1000).toLocaleDateString('en-US', dateOpts);
      }
      const d = parseApiDateTime(t);
      return d ? d.toLocaleDateString('en-US', dateOpts) : 'N/A';
    }
    return 'N/A';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Account Overview
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Subscription Status Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CreditCard color="primary" />
                <Typography variant="h6">Subscription</Typography>
              </Box>
              <Typography variant="h4" sx={{ mb: 1 }}>
                {stats.subscription?.planType || stats.subscription?.displayPlan || 'Unknown'} Plan
              </Typography>
              {stats.subscription?.contactSales && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Custom plan — unlimited usage. Contact sales for plan changes.
                </Alert>
              )}
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={
                    resolveSubscriptionAccessView(
                      stats.subscription,
                      stats.subscription?.planType || stats.subscription?.displayPlan || ''
                    ).badgeLabel
                  }
                  color={getStatusColor(
                    resolveSubscriptionAccessView(
                      stats.subscription,
                      stats.subscription?.planType || stats.subscription?.displayPlan || ''
                    ).badgeLabel.toLowerCase()
                  )}
                  size="small"
                />
              </Box>
              {(stats.subscription?.nextBillingDate != null ||
                stats.subscription?.currentPeriodEnd != null) && (
                <Typography variant="body2" color="text.secondary">
                  Next billing:{' '}
                  {formatSubscriptionDate(
                    // Prefer Stripe's current_period_end (renewal/anchor) over any computed upcoming-invoice date.
                    stats.subscription?.currentPeriodEnd ?? stats.subscription?.nextBillingDate
                  )}
                </Typography>
              )}
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 2 }}
                onClick={() => {
                  const tabs = document.querySelector('[role="tablist"]');
                  if (tabs) {
                    // Tabs order: 0=Dashboard,1=Organization,2=Team,3=Licenses,4=Subscription,5=Invoices
                    const subscriptionTab = tabs.querySelectorAll('[role="tab"]')[4] as HTMLElement;
                    subscriptionTab?.click();
                  }
                }}
              >
                Manage Subscription
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* License Usage Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <People color="primary" />
                <Typography variant="h6">Licenses</Typography>
              </Box>
              {stats.licenseUsage ? (
                <>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {stats.licenseUsage.used} / {stats.licenseUsage.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {stats.licenseUsage.available} available
                  </Typography>
                  <Box sx={{ mb: 0 }}>
                    <Box
                      sx={{
                        width: '100%',
                        height: 8,
                        bgcolor: 'grey.200',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${(stats.licenseUsage.used / stats.licenseUsage.total) * 100}%`,
                          height: '100%',
                          bgcolor:
                            stats.licenseUsage.used / stats.licenseUsage.total > 0.9
                              ? 'error.main'
                              : stats.licenseUsage.used / stats.licenseUsage.total > 0.7
                              ? 'warning.main'
                              : 'primary.main',
                        }}
                      />
                    </Box>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Recent Activity
              </Typography>
              {stats.recentActivity && stats.recentActivity.length > 0 ? (
                <List>
                  {stats.recentActivity.map((activity, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemIcon>
                          {activity.type === 'subscription' ? (
                            (() => {
                              const view = resolveSubscriptionAccessView(
                                stats.subscription,
                                stats.subscription?.planType || ''
                              );
                              return view.expired || view.cancelled ? (
                                <Warning color="warning" />
                              ) : (
                                <CheckCircle color="success" />
                              );
                            })()
                          ) : activity.type === 'team' ? (
                            <People color="primary" />
                          ) : (
                            <Warning color="warning" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.message}
                          secondary={
                            activity.type === 'subscription'
                              ? (() => {
                                  const sub = stats.subscription;
                                  if (!sub) return undefined;
                                  const periodStartSec = sub.currentPeriodStart;
                                  const periodEndSec = sub.currentPeriodEnd;
                                  const disp = (sub.displayPlan || '').toUpperCase();
                                  const planTypeU = (sub.planType || '').toUpperCase();
                                  const isFree =
                                    disp === 'FREE' || planTypeU === 'FREE';
                                  /**
                                   * Prefer billing/subscription period start (UTC epoch) when present.
                                   * FREE fallback: user.createdAt parsed as UTC LocalDateTime so 3 AM IST
                                   * (21:30 UTC previous day) displays as the correct local calendar day.
                                   */
                                  const purchaseLabel =
                                    periodStartSec != null
                                      ? formatSubscriptionDate(periodStartSec)
                                      : isFree && user?.createdAt
                                        ? formatSubscriptionDate(user.createdAt)
                                        : 'N/A';
                                  const access = resolveSubscriptionAccessView(sub, sub.planType || '');
                                  const periodEnd =
                                    periodEndSec != null ? new Date(periodEndSec * 1000) : null;

                                  if (isFree) {
                                    return (
                                      <Typography variant="body2" color="text.secondary">
                                        Purchased on: {purchaseLabel}
                                      </Typography>
                                    );
                                  }

                                  if (periodEnd) {
                                    const endLabel = access.accessPeriodPrefix;
                                    const endStr =
                                      periodEndSec != null
                                        ? formatSubscriptionDate(periodEndSec)
                                        : formatSubscriptionDate(Math.floor(periodEnd.getTime() / 1000));
                                    // FREE often gets current_period_start/end on the same day from billing;
                                    // showing both as the same "purchase" and "valid until" is misleading — treat as ongoing.
                                    return (
                                      <Box
                                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pr: 1 }}
                                      >
                                        <Typography variant="body2" color="text.secondary">
                                          Purchased on: {purchaseLabel}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {endLabel}: {endStr}
                                        </Typography>
                                      </Box>
                                    );
                                  }

                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      Purchased on: {purchaseLabel}
                                    </Typography>
                                  );
                                })()
                              : activity.type === 'team'
                                ? undefined
                                : activity.date
                                  ? new Date(activity.date).toLocaleDateString()
                                  : undefined
                          }
                        />
                      </ListItem>
                      {index < stats.recentActivity!.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recent activity
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Account Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Account Status
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    {resolveSubscriptionAccessView(stats.subscription, stats.subscription?.planType || '').expired ||
                    resolveSubscriptionAccessView(stats.subscription, stats.subscription?.planType || '').cancelled ? (
                      <Warning color="warning" />
                    ) : (
                      <CheckCircle color="success" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Subscription"
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        {(() => {
                          const view = resolveSubscriptionAccessView(
                            stats.subscription,
                            stats.subscription?.planType || ''
                          );
                          const label = view.badgeLabel;
                          const bgColor = view.cancelled ? '#ed6c02' : view.expired || label.toLowerCase() !== 'active' ? '#f44336' : '#4caf50';
                          return (
                            <Chip
                              label={label}
                              size="small"
                              sx={{
                                backgroundColor: bgColor,
                                color: 'white',
                                fontWeight: 600,
                                height: 24,
                              }}
                            />
                          );
                        })()}
                      </Box>
                    }
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    {stats.licenseUsage && stats.licenseUsage.available <= 0 ? (
                      <ErrorIcon color="error" />
                    ) : (
                      <CheckCircle color="success" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="License Availability"
                    secondary={
                      stats.licenseUsage && stats.licenseUsage.available <= 0
                        ? 'No licenses available - Purchase more'
                        : `${stats.licenseUsage?.available || 0} licenses available`
                    }
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;

